import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '__components/ToastProvider';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PluginService from 'services/PluginService';
import { Service } from './components/Service';

type AuthorizationPanelProps = {
  oAuthServices: any[];
  businessPluginApp: Record<string, any>;
  oAuthEvents: Array<any>;
  refetch: () => Promise<any>;
  inline?: boolean;
};

export const AuthorizationPanel = (props: AuthorizationPanelProps) => {
  const { t } = useTranslation();
  const {
    oAuthServices,
    businessPluginApp,
    oAuthEvents,
    refetch,
    inline = false,
  } = props;
  const [pendingRevoke, setPendingRevoke] = useState<{
    display_name: string;
    service_name: string;
  } | null>(null);

  const servicesCount = oAuthServices.length;

  const [showToast] = useToast();

  const logoutMutation = useMutation({
    mutationFn: ({
      service_name,
    }: {
      service_name: string;
      display_name: string;
    }) =>
      PluginService.revokeOauthAuthorization(
        businessPluginApp.id,
        service_name
      ),
    onSuccess: (res, variables) => {
      setPendingRevoke(null);
      showToast({
        message: t('Successfully revoked authorization for {{serviceName}}', {
          serviceName: variables.display_name,
        }),
        variant: 'success',
      });
    },
    onError: (error) => {
      showToast({
        message:
          (error as any)?.message ||
          t('Failed to revoke authorization. Please try again later.'),
        variant: 'failure',
      });
    },
    onSettled: () => {
      refetch();
    },
  });

  const businessLevelServices = useMemo(() => {
    return oAuthServices.filter((s) => s.auth_level === 'business');
  }, [oAuthServices]);

  const userLevelServices = useMemo(() => {
    return oAuthServices.filter((s) => s.auth_level === 'user');
  }, [oAuthServices]);

  return (
    <div>
      {!inline ? (
        <Typography>
          {t('This app requires authenticating with {{predicate}}:', {
            predicate:
              servicesCount > 1
                ? t('the following services')
                : t('the following service'),
          })}
        </Typography>
      ) : null}
      {businessLevelServices.length > 0 ? (
        <div>
          {!inline ? <Spacer size={20} /> : null}
          <Typography
            variant="header"
            weight="semibold"
            size={inline ? 'xl' : '2xl'}
          >
            {inline
              ? t('Required Business-Wide Authorization')
              : t('Business-Wide Authorization')}
          </Typography>
          <Spacer size={20} />
          <Typography>
            {t('{{noun}} will be available to all users in the business:', {
              noun:
                businessLevelServices.length > 1
                  ? t('These authorizations')
                  : t('This authorization'),
            })}
          </Typography>
          {businessLevelServices.map((service) => {
            return (
              <Service
                key={service.service_name}
                service={service}
                oAuthEvents={oAuthEvents}
                logoutMutation={logoutMutation}
                pendingRevoke={pendingRevoke}
                setPendingRevoke={setPendingRevoke}
                businessPluginApp={businessPluginApp}
                mode="business"
              />
            );
          })}
        </div>
      ) : null}
      {userLevelServices.length > 0 && businessLevelServices.length > 0 ? (
        <Spacer size={30} />
      ) : null}
      {userLevelServices.length > 0 ? (
        <div>
          <Spacer size={10} />
          <Typography
            variant="header"
            weight="semibold"
            size={inline ? 'xl' : '2xl'}
          >
            {inline
              ? t('Required User-Level Authorization')
              : t('User-Level Authorization')}
          </Typography>
          <Spacer size={20} />
          <Typography>
            {t('{{noun}} will only be available for your user:', {
              noun:
                userLevelServices.length > 1
                  ? t('These authorizations')
                  : t('This authorization'),
            })}
          </Typography>
          {userLevelServices.map((service) => {
            return (
              <Service
                key={service.service_name}
                service={service}
                oAuthEvents={oAuthEvents}
                logoutMutation={logoutMutation}
                pendingRevoke={pendingRevoke}
                setPendingRevoke={setPendingRevoke}
                businessPluginApp={businessPluginApp}
                mode="user"
                inline={inline}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
